import { useEffect, useState } from "react";
import {
  FormFieldItem,
  FormFieldTypesEnum,
  Response,
} from "../models/formRenderModel";
import LanguageApi from "../services/languageService";

export default function useLanguageRestrictions(): {
  applyLanguageRestrictions: (
    formFields: FormFieldItem[],
    countryValue: string
  ) => FormFieldItem[];
  isLanguageDataReady: boolean;
} {
  const [languageRestrictions, setLanguageRestrictions] = useState<Record<
    number,
    number[]
  > | null>(null);
  const [originalLanguageResponses, setOriginalLanguageResponses] = useState<
    Response[] | null
  >(null);
  const [isLanguageDataReady, setIsLanguageDataReady] =
    useState<boolean>(false);

  // Fetch languageRestrictions and originalLanguageResponses from the backend
  useEffect(() => {
    const fetchLanguageData = async () => {
      try {
        const response = await LanguageApi.getLanguageRestrictionsAndResponses();
        if (response.data) {
          if (response.data.languageRestrictions) {
            setLanguageRestrictions(response.data.languageRestrictions);
          }
          if (response.data.originalLanguageResponses) {
            setOriginalLanguageResponses(response.data.originalLanguageResponses);
          }
        }
      } catch {
        // Silently handle error - form will load without language restrictions
        // This is intentional to prevent blocking the registration form
      } finally {
        // Always mark data as ready to prevent blocking the registration form
        setIsLanguageDataReady(true);
      }
    };

    fetchLanguageData();
  }, []);

  const applyLanguageRestrictions = (
    formFields: FormFieldItem[],
    countryValue: string
  ): FormFieldItem[] => {
    const languageField = formFields?.find(
      (f) => f.itemType === FormFieldTypesEnum.IdentityLanguage
    );

    if (
      !languageField ||
      !originalLanguageResponses ||
      !isLanguageDataReady ||
      !languageRestrictions
    ) {
      return formFields;
    }
    //If the country value is empty, return the original language responses
    if (countryValue === "") {
      return formFields.map((item) =>
        item.id === languageField.id
          ? { ...item, responses: originalLanguageResponses }
          : item
      );
    }
    // If there are no restrictions for the country, return the original language responses
    let filteredResponses = originalLanguageResponses;

    const countryResponseId = parseInt(countryValue, 10);
    const hasRestrictions = languageRestrictions[countryResponseId];
    if (hasRestrictions) {
      filteredResponses = originalLanguageResponses
        .filter((r) => languageRestrictions[countryResponseId].includes(r.id))
        .sort((a, b) => a.text.localeCompare(b.text));
    }

    // Update the language field with filtered responses
    return formFields.map((item) =>
      item.id === languageField.id
        ? { ...item, responses: filteredResponses }
        : item
    );
  };

  return {
    applyLanguageRestrictions,
    isLanguageDataReady,
  };
}
